import React, { useCallback } from 'react';

import isNil from 'lodash/isNil';

import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { injectReducer } from '@lumapps/redux/manager';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { debounceThunk } from '@lumapps/redux/utils/debounceThunk';

import { actions, reducer as articlePreviewReducer } from '../../ducks/articlePreviewSlice';
import { articleEntitySelector, articleContainerIdSelector } from '../../ducks/selectors';
import { fetchArticleListByIds } from '../../ducks/thunks/fetchArticleListByIds';
import { ArticlePreviewWrapper, ArticlePreviewWrapperProps } from './ArticlePreviewWrapper';

// Dynamically inject the articlePreview reducer.
injectReducer('articlePreview', articlePreviewReducer);

const debouncedfetchArticleListByIds = debounceThunk(fetchArticleListByIds, DEBOUNCE_DELAY);
const debouncedFetchArticles = (containerId: string) => debouncedfetchArticleListByIds([containerId]);

type ConnectedArticlePreviewProps = Omit<
    ArticlePreviewWrapperProps,
    'article' | 'isLoading' | 'containerId' | 'isLegacy' | 'pushBatchIdArticle' | 'fetchArticles'
> & {
    // Several usages of this component are wrongly typed, so we need to accept any extra props.
    [key: string]: any;
};

export const ArticlePreview: React.FC<ConnectedArticlePreviewProps> = ({ id, article: articleProp, ...restProps }) => {
    const dispatch = useDispatch();

    const entity = useSelector(articleEntitySelector(id));
    const containerIdFromState = useSelector(articleContainerIdSelector);

    const isLegacy = !isNil(articleProp.isFromLegacy);
    const containerId = articleProp?.shares?.[0].container.containerId || containerIdFromState;
    const article = isLegacy ? entity : articleProp;
    const isLoading = isLegacy && entity === undefined;

    const pushBatchIdArticle = useCallback(
        (articleId: string) => {
            dispatch(actions.pushBatchIdArticle(articleId));
        },
        [dispatch],
    );

    const fetchArticles = useCallback(
        (containerId?: string) => {
            if (containerId) {
                dispatch(debouncedFetchArticles(containerId));
            }
        },
        [dispatch],
    );

    return (
        <ArticlePreviewWrapper
            id={id}
            article={article}
            isLoading={isLoading}
            containerId={containerId}
            isLegacy={isLegacy}
            pushBatchIdArticle={pushBatchIdArticle}
            fetchArticles={fetchArticles}
            {...restProps}
        />
    );
};

ArticlePreview.displayName = 'ArticlePreviewConnected';
